/**
 * Interactive decorative 3D Earth globe, built on three.js.
 * Drag / touch to spin, auto-rotates when idle.
 */
(function () {
  'use strict';

  function initGlobe(container) {
    if (!window.THREE || container.dataset.globeInitialized) return;
    container.dataset.globeInitialized = 'true';

    var canvas = container.querySelector('.globe-canvas');
    var textureUrl = container.dataset.earthTexture;
    var glowColor = container.dataset.glowColor || '#8A9BFF';
    var autoRotate = container.dataset.autoRotate === 'true';
    // Halved from the original multiplier so continents stay in view longer per spin.
    var rotateSpeed = (parseFloat(container.dataset.rotateSpeed) || 3) * 0.0003;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var width = container.clientWidth;
    var height = container.clientHeight;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    // Zoomed in so the globe (plus its glow ring) fills almost the entire
    // canvas, leaving minimal dead space between the sphere and the frame edge.
    camera.position.z = 2.85;

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    // Ensure the sRGB texture is displayed at full brightness (matches texture.colorSpace/encoding below)
    if ('outputColorSpace' in renderer) {
      renderer.outputColorSpace = THREE.SRGBColorSpace;
    } else if ('outputEncoding' in renderer && THREE.sRGBEncoding) {
      renderer.outputEncoding = THREE.sRGBEncoding;
    }

    var globeGroup = new THREE.Group();
    // Slight axial tilt, like a real globe
    globeGroup.rotation.z = (23.4 * Math.PI) / 180;
    scene.add(globeGroup);

    // Earth sphere — unlit material so the map's true colors show evenly
    // all the way around (no dark "night side" from directional shading).
    var earthGeo = new THREE.SphereGeometry(1, 48, 32);
    var earthMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
    });
    var earthMesh = new THREE.Mesh(earthGeo, earthMat);
    // Start facing a recognizable orientation (roughly the Atlantic)
    earthMesh.rotation.y = Math.PI * 1.35;
    globeGroup.add(earthMesh);

    if (textureUrl) {
      var loader = new THREE.TextureLoader();
      loader.load(textureUrl, function (texture) {
        if ('colorSpace' in texture) {
          texture.colorSpace = THREE.SRGBColorSpace;
        } else if ('encoding' in texture && THREE.sRGBEncoding) {
          texture.encoding = THREE.sRGBEncoding;
        }
        earthMat.map = texture;
        earthMat.needsUpdate = true;
      });
    }

    // Subtle atmosphere glow ring behind the globe
    var glowGeo = new THREE.RingGeometry(1.12, 1.16, 64);
    var glowMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(glowColor),
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
    });
    var glowRing = new THREE.Mesh(glowGeo, glowMat);
    scene.add(glowRing);

    // Drag-to-rotate interaction
    var isDragging = false;
    var lastX = 0;
    var lastY = 0;
    var velocityX = 0;
    var velocityY = 0;
    var idleTimer = null;
    var isIdle = true;

    function setIdle(state) {
      isIdle = state;
    }

    function onPointerDown(e) {
      isDragging = true;
      setIdle(false);
      clearTimeout(idleTimer);
      var point = e.touches ? e.touches[0] : e;
      lastX = point.clientX;
      lastY = point.clientY;
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var point = e.touches ? e.touches[0] : e;
      var deltaX = point.clientX - lastX;
      var deltaY = point.clientY - lastY;
      lastX = point.clientX;
      lastY = point.clientY;

      velocityX = deltaX * 0.005;
      velocityY = deltaY * 0.005;

      globeGroup.rotation.y += velocityX;
      globeGroup.rotation.x += velocityY;
      globeGroup.rotation.x = Math.max(-1.0, Math.min(1.0, globeGroup.rotation.x));
    }

    function onPointerUp() {
      isDragging = false;
      idleTimer = setTimeout(function () {
        setIdle(true);
      }, 1200);
    }

    container.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    container.addEventListener('touchstart', onPointerDown, { passive: true });
    window.addEventListener('touchmove', onPointerMove, { passive: true });
    window.addEventListener('touchend', onPointerUp);

    // Resize handling
    var resizeObserver = new ResizeObserver(function () {
      width = container.clientWidth;
      height = container.clientHeight;
      if (!width || !height) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    });
    resizeObserver.observe(container);

    // Render loop
    function animate() {
      requestAnimationFrame(animate);

      if (!isDragging) {
        velocityX *= 0.94;
        velocityY *= 0.94;
        globeGroup.rotation.y += velocityX;
        globeGroup.rotation.x += velocityY;

        if (autoRotate && isIdle && !reducedMotion) {
          globeGroup.rotation.y += rotateSpeed;
        }
      }

      renderer.render(scene, camera);
    }

    if (!reducedMotion) {
      animate();
    } else {
      renderer.render(scene, camera);
      container.addEventListener('mousedown', function () {
        if (!container.dataset.forcedRender) {
          animate();
          container.dataset.forcedRender = 'true';
        }
      });
    }
  }

  function initAllGlobes() {
    var containers = document.querySelectorAll('[data-globe]');
    containers.forEach(function (container) {
      if (window.THREE) {
        initGlobe(container);
      } else {
        // three.js may still be loading (deferred); poll briefly
        var attempts = 0;
        var poll = setInterval(function () {
          attempts++;
          if (window.THREE) {
            clearInterval(poll);
            initGlobe(container);
          } else if (attempts > 40) {
            clearInterval(poll);
          }
        }, 100);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGlobes);
  } else {
    initAllGlobes();
  }

  // Re-init when a merchant adds the section in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target.querySelector('[data-globe]');
    if (container) {
      if (window.THREE) {
        initGlobe(container);
      } else {
        setTimeout(function () {
          initGlobe(container);
        }, 300);
      }
    }
  });
})();
